import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import { toast } from "sonner";
import {
  Basket, ArrowRight, ArrowLeft, MapPin, Sparkle, Handshake, Storefront,
} from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";
import { readLocation } from "@/components/LocationPill";
import { fpPath } from "@/lib/site";
import SEOHead from "@/components/SEOHead";
import ListenTranslateBar from "@/components/ListenTranslateBar";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

/**
 * FarmersWhatToHarvest — /what-to-harvest
 *
 * Reverse of "What should I plant?" — the daily companion for the
 * farmer whose crops are ripening now. Uses the same climate-zone
 * calendar to surface every crop in the local harvest window with
 * Sell / Donate / List surplus CTAs.
 */
export default function FarmersWhatToHarvest() {
  const { user } = useAuth();
  const [loc, setLoc] = useState(() => {
    const s = readLocation();
    return s?.lat != null ? { lat: s.lat, lon: s.lon, label: s.city || s.country } : null;
  });
  const [month, setMonth] = useState(() => new Date().getMonth() + 1);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [farms, setFarms] = useState([]);

  useEffect(() => {
    if (!user) return;
    http.get("/farmers/farms/mine")
      .then(({ data }) => setFarms(data.farms || []))
      .catch(() => {});
  }, [user]);

  useEffect(() => {
    const on = () => {
      const s = readLocation();
      if (s?.lat != null) setLoc({ lat: s.lat, lon: s.lon, label: s.city || s.country });
      else setLoc(null);
    };
    window.addEventListener("np:location-changed", on);
    return () => window.removeEventListener("np:location-changed", on);
  }, []);

  const canQuery = loc?.lat != null;

  useEffect(() => {
    if (!canQuery) return;
    setLoading(true);
    const params = new URLSearchParams({
      latitude: loc.lat, longitude: loc.lon, month: String(month),
    });
    http.get(`/farmers/calendar?${params}`)
      .then((r) => setData(r.data))
      .catch(() => toast.error("Could not load harvest window"))
      .finally(() => setLoading(false));
  }, [loc?.lat, loc?.lon, month, canQuery]);

  const useMyLocation = () => {
    if (!navigator.geolocation) return toast.error("Geolocation not available");
    navigator.geolocation.getCurrentPosition(
      (p) => setLoc({ lat: p.coords.latitude, lon: p.coords.longitude }),
      () => toast.error("Could not read your location"),
    );
  };

  // Crops currently in the local harvest window.
  const harvestNow = useMemo(() => {
    if (!data?.crops) return [];
    return data.crops.filter((c) => c.what_to_do_now === "harvest");
  }, [data]);

  // Split into "on my farm" (highlighted) vs "regional harvest" (secondary).
  const myCropNames = useMemo(() => {
    const s = new Set();
    for (const f of farms) for (const c of f.crops || []) s.add((c || "").toLowerCase());
    return s;
  }, [farms]);

  const onMyFarm = useMemo(
    () => harvestNow.filter((c) => myCropNames.has(c.common_name.toLowerCase())),
    [harvestNow, myCropNames],
  );
  const regional = useMemo(
    () => harvestNow.filter((c) => !myCropNames.has(c.common_name.toLowerCase())),
    [harvestNow, myCropNames],
  );

  const narration = useMemo(() => {
    if (!harvestNow.length) return "";
    const parts = [
      `Harvest window — ${harvestNow.length} crops maturing in ${data?.climate_zone || "your"} climate this ${MONTHS[month-1]}.`,
    ];
    if (onMyFarm.length) parts.push(`On your farm: ${onMyFarm.slice(0,4).map((c) => c.common_name).join(", ")}.`);
    return parts.join(" ");
  }, [harvestNow, onMyFarm, data, month]);

  return (
    <main className="bg-[#F5EBDC] min-h-screen pt-32 md:pt-36 pb-32 px-5 md:px-10" data-testid="what-to-harvest-page">
      <SEOHead
        title="What Should I Harvest? — Location-Aware Harvest Calendar | FarmersPulse™"
        description="Every crop maturing in your local harvest window right now, with one-tap Sell, Donate and Surplus listing actions."
        path="/what-to-harvest"
      />
      <div className="max-w-[1100px] mx-auto">
        <Link to={fpPath("/")} className="inline-flex items-center gap-1.5 font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[#3A3830] hover:text-[#151312]">
          <ArrowLeft size={11} weight="bold" /> Farmers Pulse
        </Link>
        <div className="mt-3 font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[#2D8659] inline-flex items-center gap-1.5">
          <Basket size={12} weight="fill" /> Signature tool
        </div>
        <h1 className="mt-1 font-serif-display text-4xl md:text-5xl leading-[0.98] tracking-tight">
          What should I harvest?
        </h1>
        <p className="mt-3 max-w-2xl text-sm md:text-base text-[#3A3830]">
          Every crop currently in your local harvest window — ordered so the ones already on your farm come first. One tap to sell, donate or list surplus.
        </p>

        {narration && (
          <div className="mt-4">
            <ListenTranslateBar text={narration} testidPrefix="wth-tools" />
          </div>
        )}

        {/* Controls */}
        <section className="mt-6 rounded-2xl border border-black/10 bg-white p-4 md:p-5 grid gap-4 md:grid-cols-2" data-testid="wth-controls">
          <div>
            <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#3A3830]">Location</div>
            <div className="mt-1 flex items-center gap-2">
              <div className="flex-1 text-sm text-[#151312]" data-testid="wth-location">
                {loc ? `${loc.label || `${loc.lat.toFixed(2)}, ${loc.lon.toFixed(2)}`}` : "Not set"}
              </div>
              <button
                onClick={useMyLocation}
                data-testid="wth-locate"
                className="rounded-full border border-black/15 bg-white px-3 py-1 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:border-[#2D8659] hover:text-[#2D8659]"
              >
                <MapPin size={10} weight="fill" className="inline mr-1" /> Use GPS
              </button>
            </div>
          </div>
          <div>
            <label className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#3A3830]">Month</label>
            <select
              value={month}
              onChange={(e) => setMonth(parseInt(e.target.value, 10))}
              data-testid="wth-month"
              className="mt-1 w-full bg-white border border-black/15 rounded-lg px-3 py-2 text-sm"
            >
              {MONTHS.map((m, i) => (
                <option key={m} value={i + 1}>{m}</option>
              ))}
            </select>
          </div>
        </section>

        {/* Results */}
        <section className="mt-6" data-testid="wth-results">
          {!canQuery ? (
            <div className="rounded-2xl border border-dashed border-black/20 p-8 text-center bg-white/70">
              <MapPin size={20} weight="fill" className="mx-auto text-[#2D8659] mb-2" />
              <div className="font-serif-display text-2xl">Set your location to see what's ripening.</div>
              <button
                onClick={useMyLocation}
                data-testid="wth-locate-hero"
                className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-[#2D8659] text-white px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:bg-[#0F3D2A]"
              >
                <MapPin size={11} weight="fill" /> Use my location
              </button>
            </div>
          ) : loading ? (
            <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#8E8B7F]">Loading harvest window…</div>
          ) : harvestNow.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-black/20 p-8 text-center bg-white/70">
              <div className="font-serif-display text-xl">Nothing in the harvest window for {MONTHS[month-1]}.</div>
              <p className="mt-1 text-sm text-[#3A3830]">Try another month, or check the sow window instead.</p>
              <Link to={fpPath("/plan-what-to-plant")} className="mt-4 inline-flex items-center gap-1 rounded-full border border-[#2D8659] text-[#2D8659] px-3 py-1 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:bg-[#2D8659] hover:text-white">
                What should I plant? <ArrowRight size={10} weight="bold" />
              </Link>
            </div>
          ) : (
            <>
              <div className="mb-3 font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#2D8659]" data-testid="wth-summary">
                {harvestNow.length} in harvest window · {data.climate_zone} climate · {MONTHS[month-1]}
                {onMyFarm.length ? ` · ${onMyFarm.length} on your farm` : ""}
              </div>

              {onMyFarm.length > 0 && (
                <div className="mb-6" data-testid="wth-on-my-farm">
                  <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[#151312] mb-2">
                    <Sparkle size={11} weight="fill" className="inline mr-1 text-[#2D8659]" /> On your farm
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {onMyFarm.map((c) => <HarvestCard key={c.slug} c={c} highlighted />)}
                  </div>
                </div>
              )}

              {regional.length > 0 && (
                <div data-testid="wth-regional">
                  <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[#3A3830] mb-2">
                    Also maturing regionally
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {regional.map((c) => <HarvestCard key={c.slug} c={c} />)}
                  </div>
                </div>
              )}
            </>
          )}
        </section>
      </div>
    </main>
  );
}

function HarvestCard({ c, highlighted = false }) {
  return (
    <div
      data-testid={`wth-crop-${c.slug}`}
      className={`rounded-2xl border p-4 transition-colors ${
        highlighted
          ? "border-[#2D8659] bg-[#F0F9F3]"
          : "border-black/10 bg-white hover:border-[#2D8659]"
      }`}
    >
      <div className="flex items-center gap-2">
        <span className="text-2xl" aria-hidden>{c.emoji || "🌾"}</span>
        <div>
          <div className="font-serif-display text-xl leading-tight text-[#151312]">{c.common_name}</div>
          <div className="font-mono-tech text-[9px] tracking-[0.24em] uppercase text-[#3A3830]">
            {c.category || "crop"}
          </div>
        </div>
      </div>
      {c.harvest_notes && (
        <p className="mt-2 text-xs text-[#3A3830]">{c.harvest_notes}</p>
      )}
      <div className="mt-3 flex flex-wrap gap-1.5">
        <Link
          to={fpPath(`/crops/${c.slug}`)}
          data-testid={`wth-crop-details-${c.slug}`}
          className="rounded-full border border-black/15 bg-white px-3 py-1 font-mono-tech text-[9px] tracking-[0.24em] uppercase hover:border-[#2D8659] hover:text-[#2D8659]"
        >
          Details <ArrowRight size={9} weight="bold" className="inline ml-0.5" />
        </Link>
        <Link
          to={fpPath(`/websites/new?crop=${encodeURIComponent(c.common_name)}`)}
          data-testid={`wth-sell-${c.slug}`}
          className="rounded-full bg-[#2D8659] text-white px-3 py-1 font-mono-tech text-[9px] tracking-[0.24em] uppercase hover:bg-[#0F3D2A] inline-flex items-center gap-1"
        >
          <Storefront size={9} weight="fill" /> Sell
        </Link>
        <Link
          to={fpPath(`/surplus?crop=${encodeURIComponent(c.common_name)}`)}
          data-testid={`wth-surplus-${c.slug}`}
          className="rounded-full border border-[#0F3D2A] text-[#0F3D2A] px-3 py-1 font-mono-tech text-[9px] tracking-[0.24em] uppercase hover:bg-[#0F3D2A] hover:text-white inline-flex items-center gap-1"
        >
          <Handshake size={9} weight="fill" /> List surplus
        </Link>
      </div>
    </div>
  );
}
